import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { X, MapPin, ChevronLeft } from "lucide-react";
import { REGIONS } from "../mock/mockData";
import { useSiteContent } from "./SiteContentProvider";
import { TOUR_LINKS } from "../config/links";

const TourContext = createContext(null);

export const useTour = () => {
  const ctx = useContext(TourContext);
  return ctx || { openTour: () => {}, closeTour: () => {} };
};

export const TourProvider = ({ children }) => {
  const { locations } = useSiteContent();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // location id

  const openTour = useCallback((id = null) => {
    setSelected(id);
    setOpen(true);
  }, []);
  const closeTour = useCallback(() => setOpen(false), []);

  const ctxValue = useMemo(() => ({ openTour, closeTour }), [openTour, closeTour]);

  // lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // close on escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeLoc = selected ? locations.find((l) => l.id === selected) : null;
  const embedUrl = activeLoc ? `${TOUR_LINKS[activeLoc.id]}?embed=true` : null;

  return (
    <TourContext.Provider value={ctxValue}>
      {children}

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          {/* backdrop */}
          <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={closeTour} />

          {/* panel */}
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col">
            {/* header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-line shrink-0">
              <div className="flex items-center gap-3">
                {activeLoc && (
                  <button onClick={() => setSelected(null)} aria-label="Change location" className="text-ink-soft hover:text-camel transition-colors">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <div>
                  <p className="label-caps text-[10px] text-camel">Book a Tour</p>
                  <h3 className="font-serif-display text-2xl text-ink leading-none mt-1">
                    {activeLoc ? activeLoc.name : "Select a location"}
                  </h3>
                </div>
              </div>
              <button onClick={closeTour} aria-label="Close" className="w-9 h-9 rounded-full border border-line flex items-center justify-center text-ink hover:bg-sand transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* body */}
            <div className="overflow-y-auto">
              {!activeLoc ? (
                <div className="p-6">
                  <p className="text-ink-soft mb-6">Choose the location you'd like to tour and we'll show you available times.</p>
                  {REGIONS.map((region) => {
                    const items = locations.filter((l) => l.region === region && l.acceptingMembers !== false);
                    if (!items.length) return null;
                    return (
                      <div key={region} className="mb-7">
                        <p className="label-caps text-[10px] text-ink-soft mb-3">{region}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {items.map((l) => (
                            <button
                              key={l.id}
                              onClick={() => setSelected(l.id)}
                              className="group flex items-center gap-4 text-left p-3 rounded-2xl border border-line hover:border-camel hover:bg-sand transition-colors"
                            >
                              <span className="w-14 h-14 rounded-xl overflow-hidden shrink-0">
                                <img src={l.image} alt={l.name} className="w-full h-full object-cover" />
                              </span>
                              <span>
                                <span className="block font-serif-display text-lg text-ink group-hover:text-camel transition-colors leading-tight">{l.name}</span>
                                <span className="flex items-center gap-1 text-xs text-ink-soft mt-0.5"><MapPin className="w-3 h-3 text-camel" /> {l.city}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="w-full" style={{ height: "70vh" }}>
                  <iframe
                    title={`Book a tour at ${activeLoc.name}`}
                    src={embedUrl}
                    className="w-full h-full border-0"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
};

export default TourProvider;
